/**
 * SpotOccupancyGantt Component
 *
 * Operational GANTT view of AutoPark results showing aircraft assignments to spots.
 * Designed for printing and sharing with line managers and operations teams.
 */

import FlightLandIcon from "@mui/icons-material/FlightLand";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import PrintIcon from "@mui/icons-material/Print";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  alpha,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { addHours, format, getTime } from "date-fns";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Timeline, {
  CustomMarker,
  DateHeader,
  TimelineHeaders,
  TimelineMarkers,
} from "react-calendar-timeline";
import "react-calendar-timeline/dist/style.css";
import { useSnackbar } from "../../../../containers/SnackbarContainer";
import { Shape } from "../../../Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";
import { DecisionLogDisplay } from "../../TemporalTest/components/DecisionLogDisplay";
import {
  LogEntry,
  ParkingRequest,
  Placement,
  Spot,
  Zone,
} from "../../TemporalTest/types";
import {
  formatDuration,
  formatTimeRange,
  PartialPlacementInfo,
} from "../utils/detectPartialPlacements";
import "./SpotOccupancyGantt.css";
import { SpotSchedulePrintView } from "./SpotSchedulePrintView";

/**
 * Represents a pending change to an aircraft's schedule or spot assignment
 */
export interface ScheduleChange {
  aircraftId: string;
  tailNumber: string;
  originalArrivalBucket?: number;
  originalDepartureBucket?: number;
  originalSpotId?: string;
  newArrivalBucket?: number;
  newDepartureBucket?: number;
  newSpotId?: string;
  segmentIndex?: number | null; // null for single placements, number for segmented placements
}

interface SpotOccupancyGanttProps {
  spots: Spot[];
  aircraft: ParkingRequest[];
  placement: Placement;
  horizonHours: number;
  startTime: Date;
  currentTimestamp?: Date; // Current viewing time from main view
  rampShapes?: Shape[];
  zones?: Zone[];
  statistics?: {
    totalAircraft: number;
    placedAircraft: number;
    unplacedAircraft: number;
    partiallyPlacedAircraft?: number;
    averageMoves?: number;
  };
  partialPlacements?: PartialPlacementInfo[];
  hideStatistics?: boolean; // Hide statistics section (default: false)
  onScheduleChanged?: (changes: ScheduleChange[]) => void;
  onViewportChange?: (startTime: Date, endTime: Date) => void; // Called when viewport changes (drag/zoom)
  enableEditing?: boolean; // Enable drag-and-drop editing
  decisionLog?: LogEntry[]; // Optional: Decision log entries for aircraft placement decisions
  showDetailedView?: boolean; // Optional: Show Decision Log in tooltips (respects FBO admin setting, default: false)
}

/**
 * Format bucket to readable date/time
 */
function formatBucketTime(bucket: number, baseDate: Date): string {
  const date = addHours(baseDate, bucket * 0.25); // 15-minute buckets
  return format(date, "MMM d, HH:mm");
}

/**
 * Calculate dwell duration in human-readable format
 */
function calculateDwell(startBucket: number, endBucket: number): string {
  const totalMinutes = (endBucket - startBucket) * 15;
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/**
 * Format size category as readable label
 */
function formatSizeCategory(size: string): string {
  const labels: Record<string, string> = {
    "very-small": "Very Small",
    small: "Small",
    midsize: "Midsize",
    "super-midsize": "Super-Midsize",
    large: "Large",
    enormous: "Enormous",
  };
  return labels[size] || size;
}

/**
 * Calculate utilization percentage for a time bucket
 */
function calculateUtilization(
  spots: Spot[],
  aircraft: ParkingRequest[],
  placement: Placement,
  bucket: number
): number {
  let occupiedCount = 0;

  for (const spot of spots) {
    // Check if any aircraft occupies this spot at this time
    for (const ac of aircraft) {
      const placed = placement[ac.id];
      if (!placed) continue;

      if ("segments" in placed) {
        // Segmented placement
        for (const segment of placed.segments) {
          if (
            segment.spotId === spot.id &&
            bucket >= segment.startBucket &&
            bucket < segment.endBucket
          ) {
            occupiedCount++;
            break; // Count each spot only once
          }
        }
      } else {
        // Single placement
        if (
          placed.spotId === spot.id &&
          bucket >= ac.arrivalBucket &&
          bucket < ac.departureBucket
        ) {
          occupiedCount++;
          break;
        }
      }
    }
  }

  return spots.length > 0
    ? Math.round((occupiedCount / spots.length) * 100)
    : 0;
}

/**
 * Convert Unix timestamp to bucket index
 * Following react-calendar-timeline coordinate transformation pattern
 */
function timestampToBucket(timestamp: number, baseDate: Date): number {
  const diffMs = timestamp - getTime(baseDate);
  const diffHours = diffMs / (1000 * 60 * 60);
  return Math.round(diffHours * 4); // 4 buckets per hour (15-minute intervals)
}

/**
 * Convert bucket index to Unix timestamp
 */
function bucketToTimestamp(bucket: number, baseDate: Date): number {
  return getTime(addHours(baseDate, bucket * 0.25));
}

/**
 * Extract aircraft ID from timeline item ID
 * Handles both segmented ("ac123-segment-0") and single ("ac123") items
 */
function getAircraftIdFromItemId(itemId: string): string {
  return itemId.split("-segment-")[0];
}

/**
 * Extract segment index from timeline item ID
 * Returns null for non-segmented items
 */
function getSegmentIndexFromItemId(itemId: string): number | null {
  const parts = itemId.split("-segment-");
  return parts.length > 1 ? parseInt(parts[1], 10) : null;
}

/**
 * Find which aircraft (if any) occupies a spot at a given bucket
 * Returns aircraft ID or null
 */
function findAircraftAtSpotBucket(
  spotId: string,
  bucket: number,
  placement: Placement,
  aircraft: ParkingRequest[]
): string | null {
  for (const ac of aircraft) {
    const placed = placement[ac.id];
    if (!placed) continue;

    if ("segments" in placed) {
      // Check if any segment overlaps this bucket
      for (const seg of placed.segments) {
        if (
          seg.spotId === spotId &&
          bucket >= seg.startBucket &&
          bucket < seg.endBucket
        ) {
          return ac.id;
        }
      }
    } else {
      // Single placement
      if (
        placed.spotId === spotId &&
        bucket >= ac.arrivalBucket &&
        bucket < ac.departureBucket
      ) {
        return ac.id;
      }
    }
  }
  return null;
}

export const SpotOccupancyGantt: React.FC<SpotOccupancyGanttProps> = ({
  spots,
  aircraft,
  placement,
  horizonHours,
  startTime,
  currentTimestamp,
  rampShapes = [],
  zones,
  statistics,
  partialPlacements = [],
  hideStatistics = false,
  onScheduleChanged,
  onViewportChange,
  enableEditing = false,
  decisionLog,
  showDetailedView = false,
}) => {
  // MUI theme for dark/light mode support
  const theme = useTheme();

  // Snackbar for user feedback
  const snackbar = useSnackbar();

  // Local state to override item positions (prevents snap-back after drag)
  const [itemOverrides, setItemOverrides] = useState<
    Map<
      string,
      {
        start_time: number;
        end_time: number;
        group: string;
      }
    >
  >(new Map());

  // Track if last move had conflict (to suppress success notification)
  const [lastMoveHadConflict, setLastMoveHadConflict] = useState(false);

  // Ghost highlight state
  const [hoveredAircraft, setHoveredAircraft] = useState<ParkingRequest | null>(
    null
  );
  const [pinnedAircraft, setPinnedAircraft] = useState<ParkingRequest | null>(
    null
  );

  // Ref for timeline container (needed for width calculations)
  const timelineContainerRef = useRef<HTMLDivElement>(null);

  // Use start time as base date for timeline calculations
  const baseDate = startTime;

  // Handle hover for preview
  const handleChipMouseEnter = useCallback((ac: ParkingRequest) => {
    setHoveredAircraft(ac);
  }, []);

  const handleChipMouseLeave = useCallback(() => {
    setHoveredAircraft(null);
  }, []);

  // Handle click to pin/unpin
  const handleChipClick = useCallback((ac: ParkingRequest) => {
    setPinnedAircraft((prev) => {
      // Toggle: if clicking the same chip, unpin it
      if (prev?.id === ac.id) return null;
      // Otherwise, pin the new chip (replaces previous)
      return ac;
    });
  }, []);

  // Create aircraft map for quick lookup
  const aircraftMap = useMemo(() => {
    const map: Record<string, ParkingRequest> = {};
    aircraft.forEach((ac) => {
      map[ac.id] = ac;
    });
    return map;
  }, [aircraft]);

  // Visible time range state (for scrolling/zooming)
  // Initialize based on currentTimestamp if available, otherwise use baseDate
  const [visibleTimeStart, setVisibleTimeStart] = useState(() => {
    if (currentTimestamp) {
      const windowSize = 24 * 60 * 60 * 1000; // 24 hours window
      return getTime(currentTimestamp) - windowSize / 2;
    }
    return getTime(baseDate);
  });
  const [visibleTimeEnd, setVisibleTimeEnd] = useState(() => {
    if (currentTimestamp) {
      const windowSize = 24 * 60 * 60 * 1000; // 24 hours window
      return getTime(currentTimestamp) + windowSize / 2;
    }
    return getTime(addHours(baseDate, horizonHours));
  });

  // Update visible range when horizonHours or baseDate changes
  // BUT only if currentTimestamp is not provided (respect user's viewing context)
  useEffect(() => {
    if (!currentTimestamp) {
      setVisibleTimeStart(getTime(baseDate));
      setVisibleTimeEnd(getTime(addHours(baseDate, horizonHours)));
    }
  }, [baseDate, horizonHours, currentTimestamp]);

  // Track viewport range for display purposes (separate from data loading range)
  // Initialize to match visible range (handles currentTimestamp centering)
  const [viewportStart, setViewportStart] = useState<Date>(() => {
    if (currentTimestamp) {
      const windowSize = 24 * 60 * 60 * 1000; // 24 hours window
      return new Date(getTime(currentTimestamp) - windowSize / 2);
    }
    return baseDate;
  });
  const [viewportEnd, setViewportEnd] = useState<Date>(() => {
    if (currentTimestamp) {
      const windowSize = 24 * 60 * 60 * 1000; // 24 hours window
      return new Date(getTime(currentTimestamp) + windowSize / 2);
    }
    return addHours(baseDate, horizonHours);
  });

  // Create spot ID to name mapping
  const spotIdToName = useMemo(() => {
    const mapping: Record<string, string> = {};
    rampShapes.forEach((shape) => {
      if (shape.type === "Spot" && shape.text) {
        mapping[shape.id] = shape.text;
      }
    });
    return mapping;
  }, [rampShapes]);

  // Create spot ID to zone mapping
  const spotToZone = useMemo(() => {
    if (!zones) return {};
    const mapping: Record<string, Zone> = {};
    zones.forEach((zone) => {
      zone.spotIds.forEach((spotId) => {
        mapping[spotId] = zone;
      });
    });
    return mapping;
  }, [zones]);

  // Calculate ghost highlight position
  const calculateGhostPosition = useCallback(
    (aircraft: ParkingRequest): { left: number; width: number } | null => {
      if (!timelineContainerRef.current) return null;

      const arrivalTime = bucketToTimestamp(aircraft.arrivalBucket, baseDate);
      const departureTime = bucketToTimestamp(
        aircraft.departureBucket,
        baseDate
      );

      // Check if aircraft time overlaps visible timeline range
      if (departureTime < visibleTimeStart || arrivalTime > visibleTimeEnd) {
        return null; // Outside viewport
      }

      const totalVisibleDuration = visibleTimeEnd - visibleTimeStart;
      const leftRatio = Math.max(
        0,
        (arrivalTime - visibleTimeStart) / totalVisibleDuration
      );
      const rightRatio = Math.min(
        1,
        (departureTime - visibleTimeStart) / totalVisibleDuration
      );

      // Get timeline width (subtract sidebar width: 150px)
      const containerWidth = timelineContainerRef.current.offsetWidth;
      const timelineWidth = containerWidth - 150;

      return {
        left: 150 + leftRatio * timelineWidth, // 150 = sidebar offset
        width: (rightRatio - leftRatio) * timelineWidth,
      };
    },
    [baseDate, visibleTimeStart, visibleTimeEnd]
  );

  // Transform spots into timeline groups (sorted by name)
  const groups = useMemo(() => {
    return spots
      .map((spot) => ({
        id: spot.id,
        title: spotIdToName[spot.id] || spot.id,
      }))
      .sort((a, b) =>
        a.title.localeCompare(b.title, undefined, { numeric: true })
      );
  }, [spots, spotIdToName]);

  // Event Handlers following react-calendar-timeline patterns

  /**
   * moveResizeValidator - Validates moves/resizes and blocks conflicts
   * MUST return number (not void) - skill file pattern
   */
  const moveResizeValidator = useCallback(
    (
      action: "move" | "resize",
      item: any,
      time: number,
      resizeEdge?: "left" | "right" | null
    ): number => {
      const aircraftId = getAircraftIdFromItemId(item.id as string);
      const movingAircraft = aircraftMap[aircraftId];
      if (!movingAircraft) return time;

      const bucket = timestampToBucket(time, baseDate);

      // Calculate time range for validation
      let startBucket: number;
      let endBucket: number;
      let spotId: string;

      if (action === "move") {
        const duration =
          movingAircraft.departureBucket - movingAircraft.arrivalBucket;
        startBucket = bucket;
        endBucket = bucket + duration;
        spotId = item.group as string; // Current group during drag
        console.log(
          "[Validator] MOVE:",
          movingAircraft.tail_number,
          "to spot",
          spotId,
          "buckets",
          startBucket,
          "-",
          endBucket
        );
      } else {
        // Resize - get current position from overrides or original
        const override = itemOverrides.get(item.id as string);
        const currentGroup = override?.group || item.group;

        if (resizeEdge === "left") {
          startBucket = bucket;
          endBucket = override
            ? timestampToBucket(override.end_time, baseDate)
            : movingAircraft.departureBucket;
        } else {
          startBucket = override
            ? timestampToBucket(override.start_time, baseDate)
            : movingAircraft.arrivalBucket;
          endBucket = bucket;
        }
        spotId = currentGroup as string;
      }

      // Find the spot
      const spot = spots.find((s) => s.id === spotId);
      if (!spot) {
        console.log("[Validator] Spot not found:", spotId);
        snackbar.notify({ text: "Spot not found", severity: "error" });
        return time; // Allow anyway
      }

      console.log(
        "[Validator] Checking spot",
        spotId,
        "occupancy length:",
        spot.occupancy.length
      );

      // Check size compatibility (only for moves, not resizes)
      let hasConflict = false;
      if (
        action === "move" &&
        !spot.allowedSizes.includes(movingAircraft.sizeCategory)
      ) {
        hasConflict = true;
        const allowedSizesStr = spot.allowedSizes
          .map(formatSizeCategory)
          .join(", ");
        snackbar.notify({
          text: `Warning: Spot ${
            spotIdToName[spotId] || spotId
          } only allows ${allowedSizesStr} (${
            movingAircraft.tail_number
          } is ${formatSizeCategory(movingAircraft.sizeCategory)})`,
          severity: "warning",
        });
        // Continue to check occupancy, might have multiple issues
      }

      // Check occupancy bitmap for conflicts
      for (let b = startBucket; b < endBucket; b++) {
        const wordIndex = Math.floor(b / 64);
        const bitIndex = b % 64;

        if (wordIndex < spot.occupancy.length) {
          const occupied = !!(
            spot.occupancy[wordIndex] &
            (BigInt(1) << BigInt(bitIndex))
          );

          if (occupied) {
            // Find which aircraft occupies this bucket
            const occupyingAircraftId = findAircraftAtSpotBucket(
              spotId,
              b,
              placement,
              aircraft
            );
            const isSameAircraft = occupyingAircraftId === aircraftId;
            console.log(
              "[Validator] Bucket",
              b,
              "occupied by:",
              occupyingAircraftId,
              "moving aircraft:",
              aircraftId,
              "same?:",
              isSameAircraft
            );

            if (occupyingAircraftId && !isSameAircraft) {
              // Different aircraft - show warning
              hasConflict = true;
              const occupyingAircraft = aircraftMap[occupyingAircraftId];
              const conflictTime = formatBucketTime(b, baseDate);
              console.log(
                "[Validator] CONFLICT DETECTED at",
                conflictTime,
                "with",
                occupyingAircraft?.tail_number
              );
              snackbar.notify({
                text: `Warning: Spot ${
                  spotIdToName[spotId] || spotId
                } occupied by ${
                  occupyingAircraft?.tail_number || "another aircraft"
                } at ${conflictTime}`,
                severity: "warning",
              });
              // Show warning once, then allow move
              break;
            }
          }
        }
      }

      console.log(
        "[Validator] Final result - hasConflict:",
        hasConflict,
        "returning:",
        time
      );

      // Track conflict state to suppress success notification
      setLastMoveHadConflict(hasConflict);

      // Return new time (allow move even with conflicts)
      return time;
    },
    [
      aircraftMap,
      aircraft,
      baseDate,
      spots,
      placement,
      snackbar,
      spotIdToName,
      itemOverrides,
    ]
  );

  /**
   * onItemMove - Called on drop, apply changes immediately
   */
  const handleItemMove = useCallback(
    (itemId: any, dragTime: number, newGroupOrder: number) => {
      const group = groups[newGroupOrder];
      const newSpotId = group.id as string;

      const aircraftId = getAircraftIdFromItemId(itemId as string);
      const segmentIndex = getSegmentIndexFromItemId(itemId as string);
      const aircraft = aircraftMap[aircraftId];
      if (!aircraft) return;

      // Convert timestamp to bucket
      const newArrivalBucket = timestampToBucket(dragTime, baseDate);
      const duration = aircraft.departureBucket - aircraft.arrivalBucket;
      const newDepartureBucket = newArrivalBucket + duration;

      // Get original spot ID (for segmented, get the specific segment's spot)
      let originalSpotId: string;
      if ("segments" in placement[aircraftId]) {
        const idx = segmentIndex !== null ? segmentIndex : 0;
        originalSpotId = placement[aircraftId].segments[idx].spotId;
      } else {
        originalSpotId = placement[aircraftId].spotId;
      }

      // Build schedule change
      const change: ScheduleChange = {
        aircraftId,
        tailNumber: aircraft.tail_number,
        originalArrivalBucket: aircraft.arrivalBucket,
        originalDepartureBucket: aircraft.departureBucket,
        originalSpotId,
        newArrivalBucket,
        newDepartureBucket,
        newSpotId,
        segmentIndex,
      };

      // Update local item overrides for immediate visual update (prevents snap-back)
      setItemOverrides((prev) =>
        new Map(prev).set(itemId as string, {
          start_time: dragTime,
          end_time:
            dragTime +
            (aircraft.departureBucket - aircraft.arrivalBucket) *
              15 *
              60 *
              1000,
          group: newSpotId,
        })
      );

      // Call parent callback for persistence
      if (onScheduleChanged) {
        onScheduleChanged([change]);
      }

      // Only show success notification if there was no conflict
      if (!lastMoveHadConflict) {
        snackbar.notify({
          text: `Moved ${aircraft.tail_number} to ${
            spotIdToName[newSpotId] || newSpotId
          }`,
          severity: "success",
        });
      }
    },
    [
      aircraftMap,
      baseDate,
      placement,
      onScheduleChanged,
      snackbar,
      groups,
      spotIdToName,
      lastMoveHadConflict,
    ]
  );

  /**
   * onItemResize - Called on resize complete
   */
  const handleItemResize = useCallback(
    (itemId: any, time: number, edge: "left" | "right") => {
      const aircraftId = getAircraftIdFromItemId(itemId as string);
      const segmentIndex = getSegmentIndexFromItemId(itemId as string);
      const aircraft = aircraftMap[aircraftId];
      if (!aircraft) return;

      const bucket = timestampToBucket(time, baseDate);

      // Get current item position (from overrides OR original placement)
      const override = itemOverrides.get(itemId as string);
      let originalSpotId: string;
      let currentStartBucket: number;
      let currentEndBucket: number;

      if (override) {
        // Use overridden values
        originalSpotId = override.group;
        currentStartBucket = timestampToBucket(override.start_time, baseDate);
        currentEndBucket = timestampToBucket(override.end_time, baseDate);
      } else {
        // Use original placement values (specific segment if segmented)
        if ("segments" in placement[aircraftId]) {
          const idx = segmentIndex !== null ? segmentIndex : 0;
          const segment = placement[aircraftId].segments[idx];
          originalSpotId = segment.spotId;
          currentStartBucket = segment.startBucket;
          currentEndBucket = segment.endBucket;
        } else {
          originalSpotId = placement[aircraftId].spotId;
          currentStartBucket = aircraft.arrivalBucket;
          currentEndBucket = aircraft.departureBucket;
        }
      }

      // Calculate new buckets based on resize edge
      const newArrivalBucket = edge === "left" ? bucket : currentStartBucket;
      const newDepartureBucket = edge === "right" ? bucket : currentEndBucket;

      // Build schedule change
      const change: ScheduleChange = {
        aircraftId,
        tailNumber: aircraft.tail_number,
        originalArrivalBucket: currentStartBucket,
        originalDepartureBucket: currentEndBucket,
        originalSpotId,
        newArrivalBucket,
        newDepartureBucket,
        newSpotId: originalSpotId,
        segmentIndex,
      };

      // Calculate new timestamps from buckets
      const newStartTime = bucketToTimestamp(newArrivalBucket, baseDate);
      const newEndTime = bucketToTimestamp(newDepartureBucket, baseDate);

      // Update local item overrides for immediate visual update (prevents snap-back)
      setItemOverrides((prev) =>
        new Map(prev).set(itemId as string, {
          start_time: newStartTime,
          end_time: newEndTime,
          group: originalSpotId,
        })
      );

      // Call parent callback for persistence
      if (onScheduleChanged) {
        onScheduleChanged([change]);
      }

      // Only show success notification if there was no conflict
      if (!lastMoveHadConflict) {
        snackbar.notify({
          text: `Adjusted ${aircraft.tail_number} schedule`,
          severity: "success",
        });
      }
    },
    [
      aircraftMap,
      baseDate,
      placement,
      onScheduleChanged,
      snackbar,
      lastMoveHadConflict,
      itemOverrides,
    ]
  );

  // Transform placements into timeline items
  const items = useMemo(() => {
    const timelineItems: any[] = [];

    aircraft.forEach((ac) => {
      const placed = placement[ac.id];
      if (!placed) return;

      // Convert time buckets to actual timestamps
      const bucketToTime = (bucket: number) => {
        return getTime(addHours(baseDate, bucket * 0.25)); // 15-minute buckets
      };

      if ("segments" in placed) {
        // Segmented placement - create item for each segment
        placed.segments.forEach((segment, idx) => {
          const itemId = `${ac.id}-segment-${idx}`;

          // Check if adjacent segments are in staging zones
          const prevSegment = idx > 0 ? placed.segments[idx - 1] : null;
          const nextSegment =
            idx < placed.segments.length - 1 ? placed.segments[idx + 1] : null;

          const prevSpot = prevSegment
            ? spots.find((s) => s.id === prevSegment.spotId)
            : null;
          const nextSpot = nextSegment
            ? spots.find((s) => s.id === nextSegment.spotId)
            : null;

          const prevSegmentIsStaging =
            prevSpot?.shape?.metadata?.is_staging === true;
          const nextSegmentIsStaging =
            nextSpot?.shape?.metadata?.is_staging === true;

          const baseItem = {
            id: itemId,
            group: segment.spotId,
            title: ac.tail_number,
            start_time: bucketToTime(segment.startBucket),
            end_time: bucketToTime(segment.endBucket),
            aircraft: ac,
            moveCount: placed.moveCount,
            isSegmented: true,
            segmentIndex: idx,
            totalSegments: placed.segments.length,
            prevSegmentIsStaging,
            nextSegmentIsStaging,
          };

          // Apply local overrides if they exist (for immediate visual update)
          const override = itemOverrides.get(itemId);
          if (override) {
            baseItem.start_time = override.start_time;
            baseItem.end_time = override.end_time;
            baseItem.group = override.group;
          }

          timelineItems.push(baseItem);
        });
      } else {
        // Single placement
        const itemId = ac.id;
        const baseItem = {
          id: itemId,
          group: placed.spotId,
          title: ac.tail_number,
          start_time: bucketToTime(ac.arrivalBucket),
          end_time: bucketToTime(ac.departureBucket),
          aircraft: ac,
          moveCount: 0,
          isSegmented: false,
        };

        // Apply local overrides if they exist (for immediate visual update)
        const override = itemOverrides.get(itemId);
        if (override) {
          baseItem.start_time = override.start_time;
          baseItem.end_time = override.end_time;
          baseItem.group = override.group;
        }

        timelineItems.push(baseItem);
      }
    });

    return timelineItems;
  }, [aircraft, placement, baseDate, itemOverrides, spots]);

  // Calculate average utilization
  const avgUtilization = useMemo(() => {
    if (!spots.length || !horizonHours) return 0;

    const totalBuckets = horizonHours * 4; // 15-minute buckets
    let totalUtilization = 0;

    for (let bucket = 0; bucket < totalBuckets; bucket++) {
      totalUtilization += calculateUtilization(
        spots,
        aircraft,
        placement,
        bucket
      );
    }

    return Math.round(totalUtilization / totalBuckets);
  }, [spots, aircraft, placement, horizonHours]);

  // Custom item renderer
  const itemRenderer = ({ item, itemContext, getItemProps }: any) => {
    const aircraft = item.aircraft;
    const isSegmented = item.isSegmented;
    const moveCount = item.moveCount || 0;

    // Determine if this is a staging segment
    const currentSpot = spots.find((s) => s.id === item.group);
    const isInStagingZone = currentSpot?.shape?.metadata?.is_staging === true;
    let stagingSegmentType: "arrival" | "departure" | null = null;

    if (isSegmented && isInStagingZone && item.segmentIndex !== undefined) {
      // First segment in staging zone = arrival, last = departure
      if (item.segmentIndex === 0) {
        stagingSegmentType = "arrival";
      } else if (item.segmentIndex === item.totalSegments - 1) {
        stagingSegmentType = "departure";
      }
    }

    // Check if this item is being dragged
    const isDragging = itemContext.dragging;
    const dragTime = itemContext.dragTime;
    const dragGroupDelta = itemContext.dragGroupDelta;

    // Calculate preview values during drag
    let previewArrivalBucket = aircraft.arrivalBucket;
    let previewDepartureBucket = aircraft.departureBucket;
    let previewSpotName = spotIdToName[item.group] || item.group;

    if (isDragging && dragTime !== null) {
      previewArrivalBucket = timestampToBucket(dragTime, baseDate);
      const duration = aircraft.departureBucket - aircraft.arrivalBucket;
      previewDepartureBucket = previewArrivalBucket + duration;

      if (dragGroupDelta !== null && dragGroupDelta !== undefined) {
        const newGroupIndex =
          itemContext.dimensions.order.index + dragGroupDelta;
        const newGroup = groups[newGroupIndex];
        if (newGroup) {
          previewSpotName = newGroup.title as string;
        }
      }
    }

    // Build tooltip content
    const tooltipContent = (
      <Box>
        {/* Header Section - Aircraft Identity */}
        <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: 0.5 }}
          >
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              sx={{ fontSize: "0.95rem" }}
            >
              {aircraft.is_locked
                ? "🔒 "
                : aircraft.placement_source === "auto"
                ? "⚡ "
                : aircraft.placement_source === "manual"
                ? "✋ "
                : ""}
              {aircraft.tail_number}
            </Typography>
            <Chip
              label={formatSizeCategory(aircraft.sizeCategory)}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: "0.65rem", fontWeight: 500, height: 20 }}
            />
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ fontSize: "0.8rem" }}
          >
            {aircraft.aircraft?.make}{" "}
            {aircraft.aircraft?.model || "Unknown Type"}
          </Typography>
        </Box>

        {/* Move Count Chip (if applicable) */}
        {moveCount > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Chip
              label={`${moveCount} move${moveCount !== 1 ? "s" : ""}`}
              size="small"
              color="warning"
              icon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
              sx={{ fontSize: "0.7rem", fontWeight: 500 }}
            />
          </Box>
        )}

        {/* Staging Segment Type (if applicable) */}
        {stagingSegmentType && (
          <Box sx={{ mb: 1.5 }}>
            <Chip
              label={
                stagingSegmentType === "arrival"
                  ? "Arrival Staging"
                  : "Departure Staging"
              }
              size="small"
              color="warning"
              variant="outlined"
              sx={{
                fontSize: "0.7rem",
                fontWeight: 500,
                bgcolor: "#FF9800",
                color: "white",
                borderColor: "#F57C00",
              }}
            />
          </Box>
        )}

        {/* Location Section */}
        <Box sx={{ mb: 1.5 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 0.5 }}
          >
            <LocalParkingIcon
              sx={{
                fontSize: 16,
                color: isDragging ? "primary.dark" : "primary.main",
              }}
            />
            <Typography
              variant="body2"
              fontWeight={600}
              color={isDragging ? "primary.dark" : "inherit"}
            >
              {previewSpotName}
            </Typography>
            {isSegmented && (
              <Chip
                label={`${item.segmentIndex + 1}/${item.totalSegments}`}
                size="small"
                sx={{ height: 18, fontSize: "0.65rem" }}
              />
            )}
          </Stack>
          {spotToZone[item.group] && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ pl: 3, fontSize: "0.75rem" }}
            >
              Zone: {spotToZone[item.group].name}
            </Typography>
          )}
        </Box>

        {/* Time Section */}
        <Box sx={{ pt: 1.5, borderTop: 1, borderColor: "divider" }}>
          {isSegmented && (
            <>
              {/* Segment-Specific Times (In This Spot) */}
              <Box sx={{ mb: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    In This Spot
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{ fontSize: "0.75rem", fontStyle: "italic" }}
                  >
                    {calculateDwell(
                      isDragging && dragTime !== null
                        ? timestampToBucket(dragTime, baseDate)
                        : timestampToBucket(item.start_time, baseDate),
                      isDragging && dragTime !== null
                        ? timestampToBucket(dragTime, baseDate) +
                            (timestampToBucket(item.end_time, baseDate) -
                              timestampToBucket(item.start_time, baseDate))
                        : timestampToBucket(item.end_time, baseDate)
                    )}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FlightLandIcon sx={{ fontSize: 16 }} />
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.8rem" }}
                      color={isDragging ? "primary.dark" : "inherit"}
                    >
                      {isDragging && dragTime !== null
                        ? formatBucketTime(
                            timestampToBucket(dragTime, baseDate),
                            baseDate
                          )
                        : formatBucketTime(
                            timestampToBucket(item.start_time, baseDate),
                            baseDate
                          )}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FlightTakeoffIcon sx={{ fontSize: 16 }} />
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.8rem" }}
                      color={isDragging ? "primary.dark" : "inherit"}
                    >
                      {isDragging && dragTime !== null
                        ? formatBucketTime(
                            timestampToBucket(dragTime, baseDate) +
                              (timestampToBucket(item.end_time, baseDate) -
                                timestampToBucket(item.start_time, baseDate)),
                            baseDate
                          )
                        : formatBucketTime(
                            timestampToBucket(item.end_time, baseDate),
                            baseDate
                          )}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              {/* Overall Trip Times */}
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Overall Trip
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{ fontSize: "0.75rem", fontStyle: "italic" }}
                  >
                    {calculateDwell(
                      aircraft.arrivalBucket,
                      aircraft.departureBucket
                    )}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FlightLandIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption" sx={{ fontSize: "0.8rem" }}>
                      {formatBucketTime(aircraft.arrivalBucket, baseDate)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FlightTakeoffIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption" sx={{ fontSize: "0.8rem" }}>
                      {formatBucketTime(aircraft.departureBucket, baseDate)}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </>
          )}

          {!isSegmented && (
            /* Single Placement - Show only one time section */
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <FlightLandIcon sx={{ fontSize: 16 }} />
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.8rem" }}
                  color={isDragging ? "primary.dark" : "inherit"}
                >
                  {formatBucketTime(previewArrivalBucket, baseDate)}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <FlightTakeoffIcon sx={{ fontSize: 16 }} />
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.8rem" }}
                  color={isDragging ? "primary.dark" : "inherit"}
                >
                  {formatBucketTime(previewDepartureBucket, baseDate)}
                </Typography>
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  pl: 3,
                  pt: 0.5,
                  fontSize: "0.75rem",
                  fontStyle: "italic",
                }}
              >
                {calculateDwell(previewArrivalBucket, previewDepartureBucket)}
              </Typography>
            </Stack>
          )}

          {isDragging && (
            <Typography
              variant="caption"
              color="primary.dark"
              sx={{ display: "block", mt: 1, fontStyle: "italic" }}
            >
              Moving...
            </Typography>
          )}
        </Box>
      </Box>
    );

    // Color based on placement source and type
    // Adjacent staging info from item
    const prevSegmentIsStaging = item.prevSegmentIsStaging;
    const nextSegmentIsStaging = item.nextSegmentIsStaging;

    // Determine if this is a staging segment (in staging zone)
    const isStagingSegment = isInStagingZone;

    // Base colors for auto/manual placements
    const autoFillColor = "#4287F5"; // Blue
    const manualFillColor = "#FB8C00"; // Orange
    const stagingFillColor =
      aircraft.placement_source === "manual" ? "#9C27B0" : "#8B5CF6"; // Purple variants

    const autoBorderColor = "#2563EB"; // Dark blue
    const manualBorderColor = "#E65100"; // Dark orange
    const stagingBorderColor =
      aircraft.placement_source === "manual" ? "#7B1FA2" : "#6D28D9"; // Purple variants

    let backgroundColor: string;
    let normalBorderColor: string;

    if (isDragging) {
      backgroundColor = "#64B5F6"; // Lighter blue during drag
      normalBorderColor =
        aircraft.placement_source === "manual" ? "#FF6F00" : "#1976D2";
    } else if (isStagingSegment) {
      // Staging segments: purple fill and borders
      backgroundColor = stagingFillColor;
      normalBorderColor = stagingBorderColor;
    } else if (isSegmented) {
      // Non-staging segmented: normal fill color (not purple)
      backgroundColor =
        aircraft.placement_source === "manual"
          ? manualFillColor
          : autoFillColor;
      normalBorderColor =
        aircraft.placement_source === "manual"
          ? manualBorderColor
          : autoBorderColor;
    } else {
      // Single placements: normal colors
      backgroundColor =
        aircraft.placement_source === "manual"
          ? manualFillColor
          : autoFillColor;
      normalBorderColor =
        aircraft.placement_source === "manual"
          ? manualBorderColor
          : autoBorderColor;
    }

    // Calculate individual border colors
    // For non-staging segments, borders adjacent to staging get purple color
    const topBorderColor = normalBorderColor;
    const bottomBorderColor = normalBorderColor;
    const leftBorderColor =
      isSegmented && !isStagingSegment && prevSegmentIsStaging
        ? stagingBorderColor
        : normalBorderColor;
    const rightBorderColor =
      isSegmented && !isStagingSegment && nextSegmentIsStaging
        ? stagingBorderColor
        : normalBorderColor;

    // Calculate background (solid or gradient for staging-adjacent segments)
    let background: string;
    if (
      !isStagingSegment &&
      isSegmented &&
      (prevSegmentIsStaging || nextSegmentIsStaging)
    ) {
      // Non-staging segment adjacent to staging: use gradient
      const purpleColor = stagingFillColor;
      if (prevSegmentIsStaging && nextSegmentIsStaging) {
        // Both sides adjacent to staging
        background = `linear-gradient(to right, ${purpleColor} 0%, ${backgroundColor} 15%, ${backgroundColor} 85%, ${purpleColor} 100%)`;
      } else if (prevSegmentIsStaging) {
        // Left side adjacent to staging
        background = `linear-gradient(to right, ${purpleColor} 0%, ${backgroundColor} 15%, ${backgroundColor} 100%)`;
      } else {
        // Right side adjacent to staging
        background = `linear-gradient(to left, ${purpleColor} 0%, ${backgroundColor} 15%, ${backgroundColor} 100%)`;
      }
    } else {
      // Solid color for all other cases
      background = backgroundColor;
    }

    const borderWidth = isDragging ? 2 : 1;

    const itemProps = getItemProps({
      style: {
        background,
        borderRadius: 4,
        borderTopWidth: borderWidth,
        borderBottomWidth: borderWidth,
        borderLeftWidth: borderWidth,
        borderRightWidth: borderWidth,
        borderStyle: "solid",
        borderTopColor: topBorderColor,
        borderBottomColor: bottomBorderColor,
        borderLeftColor: leftBorderColor,
        borderRightColor: rightBorderColor,
        color: "#fff",
        fontSize: 12,
        padding: "4px 8px",
        cursor: enableEditing ? "move" : "default",
        fontWeight: 500,
      },
    });

    const { key, title: _title, ...restProps } = itemProps;

    return (
      <Tooltip
        title={tooltipContent}
        arrow
        placement="top"
        key={key}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: "flip",
                enabled: true,
                options: {
                  fallbackPlacements: ["bottom", "right", "left"],
                  padding: 100, // Large padding to trigger flip sooner (tooltip is ~300px tall)
                },
              },
              {
                name: "preventOverflow",
                enabled: true,
                options: {
                  padding: 8,
                  altAxis: true, // Also prevent horizontal overflow
                },
              },
            ],
          },
          tooltip: {
            sx: {
              bgcolor: "background.paper",
              color: "text.primary",
              boxShadow: 3,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              maxWidth: 360,
              "& .MuiTooltip-arrow": {
                color: "background.paper",
                "&::before": {
                  border: "1px solid",
                  borderColor: "divider",
                },
              },
            },
          },
        }}
      >
        <div
          {...restProps}
          style={{
            ...restProps.style,
            height: itemContext.dimensions.height,
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.title}
            {isSegmented && ` (${item.segmentIndex + 1}/${item.totalSegments})`}
          </div>
        </div>
      </Tooltip>
    );
  };

  // Find rejected log entry for an aircraft
  const getRejectedLogEntry = useCallback(
    (aircraftId: string): LogEntry | null => {
      if (!decisionLog) return null;

      return (
        decisionLog.find(
          (entry) =>
            entry.type === "rejected" && entry.aircraftId === aircraftId
        ) || null
      );
    },
    [decisionLog]
  );

  // Build tooltip content for unplaced aircraft
  const buildUnplacedAircraftTooltip = useCallback(
    (ac: ParkingRequest) => {
      const rejectedEntry = getRejectedLogEntry(ac.id);

      return (
        <Box>
          {/* Header Section - Aircraft Identity */}
          <Box
            sx={{ mb: 1.5, pb: 1.5, borderBottom: 1, borderColor: "divider" }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ mb: 0.5 }}
            >
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{ fontSize: "0.95rem" }}
              >
                {ac.is_locked
                  ? "🔒 "
                  : ac.placement_source === "auto"
                  ? "⚡ "
                  : ac.placement_source === "manual"
                  ? "✋ "
                  : ""}
                {ac.tail_number}
              </Typography>
              <Chip
                label={formatSizeCategory(ac.sizeCategory)}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: "0.65rem", fontWeight: 500, height: 20 }}
              />
            </Stack>
            {ac.aircraft && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ fontSize: "0.8rem" }}
              >
                {ac.aircraft.make} {ac.aircraft.model || "Unknown Type"}
              </Typography>
            )}
          </Box>

          {/* Time Section */}
          <Box sx={{ pt: 1.5, borderTop: 1, borderColor: "divider" }}>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <FlightLandIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontSize: "0.8rem" }}>
                  {formatBucketTime(ac.arrivalBucket, baseDate)}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <FlightTakeoffIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontSize: "0.8rem" }}>
                  {formatBucketTime(ac.departureBucket, baseDate)}
                </Typography>
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  pl: 3,
                  pt: 0.5,
                  fontSize: "0.75rem",
                  fontStyle: "italic",
                }}
              >
                {calculateDwell(ac.arrivalBucket, ac.departureBucket)}
              </Typography>
            </Stack>

            {/* Optional Trip ID */}
            {ac.trip_id && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mt: 1.5,
                  pt: 1.5,
                  borderTop: 1,
                  borderColor: "divider",
                  fontSize: "0.75rem",
                }}
              >
                Trip ID: {ac.trip_id}
              </Typography>
            )}
          </Box>

          {/* Decision Log Section (only when showDetailedView enabled) */}
          {showDetailedView && rejectedEntry && (
            <Box
              sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: "divider" }}
            >
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  display: "block",
                  mb: 1,
                  color: "error.main",
                }}
              >
                Why Unplaced:
              </Typography>
              <DecisionLogDisplay
                aircraft={ac}
                log={[rejectedEntry]}
                spotIdToName={spotIdToName}
                compact={true}
              />
            </Box>
          )}
        </Box>
      );
    },
    [baseDate, getRejectedLogEntry, showDetailedView, spotIdToName]
  );

  // Handle timeline scroll/zoom
  const handleTimeChange = useCallback(
    (
      newVisibleTimeStart: number,
      newVisibleTimeEnd: number,
      updateScrollCanvas: any
    ) => {
      setVisibleTimeStart(newVisibleTimeStart);
      setVisibleTimeEnd(newVisibleTimeEnd);
      updateScrollCanvas(newVisibleTimeStart, newVisibleTimeEnd);

      // Update viewport display
      setViewportStart(new Date(newVisibleTimeStart));
      setViewportEnd(new Date(newVisibleTimeEnd));

      // Notify parent to update input fields
      if (onViewportChange) {
        onViewportChange(
          new Date(newVisibleTimeStart),
          new Date(newVisibleTimeEnd)
        );
      }
    },
    [onViewportChange]
  );

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // Custom label format for timeline headers
  const customLabelFormat = (
    [startTime]: [any, any],
    unit: string,
    labelWidth: number
  ): string => {
    if (unit === "day") {
      return labelWidth < 60
        ? startTime.format("D")
        : startTime.format("ddd D");
    }
    if (unit === "hour") {
      return startTime.format("HH:00");
    }
    if (unit === "minute") {
      return startTime.format("HH:mm");
    }
    if (unit === "month") {
      return startTime.format("MMMM");
    }
    return startTime.format("L");
  };

  // Format date range for print header
  const dateRangeString = `${format(baseDate, "MMM d, yyyy")} - ${format(
    addHours(baseDate, horizonHours),
    "MMM d, yyyy"
  )}`;
  const printDateString = format(new Date(), "MMM d, yyyy h:mm a");

  return (
    <Stack
      className="spot-occupancy-gantt"
      direction="column"
      height="100%"
      data-ramp-name="Ramp" // TODO: Pass actual ramp name via props
      data-date-range={dateRangeString}
      data-print-date={printDateString}
    >
      {/* Statistics Header */}
      {!hideStatistics && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            backgroundColor: "background.default",
            border: 1,
            borderColor: "divider",
          }}
          className="gantt-statistics"
        >
          <Stack
            direction="row"
            spacing={4}
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={4}>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Aircraft Placed
                </Typography>
                <Typography variant="h6" component="div">
                  {statistics?.placedAircraft || 0} /{" "}
                  {statistics?.totalAircraft || 0}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Avg Utilization
                </Typography>
                <Typography variant="h6" component="div">
                  {avgUtilization}%
                </Typography>
              </Box>
              {statistics?.averageMoves !== undefined &&
                statistics.averageMoves > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Avg Moves
                    </Typography>
                    <Typography variant="h6" component="div">
                      {statistics.averageMoves.toFixed(1)}
                    </Typography>
                  </Box>
                )}
            </Stack>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              className="print-button"
              size="small"
            >
              Print
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Viewport Indicator */}
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Currently viewing: {format(viewportStart, "MMM d, yyyy HH:mm")} -{" "}
          {format(viewportEnd, "MMM d, yyyy HH:mm")}
        </Typography>
      </Box>

      {/* Timeline */}
      <Box
        ref={timelineContainerRef}
        className="gantt-timeline-container"
        sx={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          overflowY: "auto",
          overflowX: "auto",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          // CSS custom properties for react-calendar-timeline dark/light mode
          "--timeline-bg": theme.palette.background.paper,
          "--timeline-bg-default": theme.palette.background.default,
          "--timeline-border": theme.palette.divider,
          "--timeline-text-primary": theme.palette.text.primary,
          "--timeline-text-secondary": theme.palette.text.secondary,
          "--timeline-header-bg": theme.palette.background.paper,
          "--timeline-row-hover": theme.palette.action.hover,
          "--timeline-grid-line": theme.palette.divider,
          "--timeline-weekend-bg": theme.palette.action.selected,
        }}
      >
        {/* Ghost Highlight Overlay */}
        {(() => {
          // Determine which aircraft to show (pinned takes priority over hover for consistency)
          const displayAircraft = pinnedAircraft || hoveredAircraft;
          if (!displayAircraft) return null;

          const position = calculateGhostPosition(displayAircraft);
          if (!position) return null;

          const isPinned = pinnedAircraft?.id === displayAircraft.id;
          const isDark = theme.palette.mode === "dark";
          const primaryColor = theme.palette.primary.main;

          return (
            <Box
              sx={{
                position: "absolute",
                left: position.left,
                width: position.width,
                top: 0,
                bottom: 0,
                zIndex: 49, // Below timeline items (z-index 50+)
                background: `linear-gradient(to bottom, ${alpha(
                  primaryColor,
                  isDark ? 0.15 : 0.12
                )}, ${alpha(primaryColor, isDark ? 0.08 : 0.06)})`,
                borderLeft: `2px dashed ${alpha(
                  primaryColor,
                  isDark ? 0.4 : 0.35
                )}`,
                borderRight: `2px dashed ${alpha(
                  primaryColor,
                  isDark ? 0.4 : 0.35
                )}`,
                borderTop: `1px solid ${alpha(primaryColor, 0.25)}`,
                pointerEvents: "none", // Critical: doesn't block timeline interactions
                transition:
                  "opacity 200ms ease-out, width 200ms ease-out, left 200ms ease-out",
                opacity: 1,
              }}
            >
              {/* Tail number badge (only show on pinned highlights) */}
              {isPinned && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    bgcolor: alpha(theme.palette.background.paper, 0.95),
                    border: `1px solid ${alpha(primaryColor, 0.3)}`,
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    fontSize: 11,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    boxShadow: 1,
                  }}
                >
                  ✈ {displayAircraft.tail_number}
                </Box>
              )}
            </Box>
          );
        })()}

        <Timeline
          groups={groups}
          items={items}
          keys={{
            groupIdKey: "id",
            groupTitleKey: "title",
            groupLabelKey: "title",
            groupRightTitleKey: "rightTitle",
            itemIdKey: "id",
            itemTitleKey: "title",
            itemDivTitleKey: "divTitle", // Use non-existent property to avoid native title attribute (conflicts with MUI Tooltip)
            itemGroupKey: "group",
            itemTimeStartKey: "start_time",
            itemTimeEndKey: "end_time",
          }}
          visibleTimeStart={visibleTimeStart}
          visibleTimeEnd={visibleTimeEnd}
          // Drag-and-drop configuration (skill file patterns)
          canMove={enableEditing}
          canResize={enableEditing ? "both" : false}
          canChangeGroup={enableEditing}
          canSelect={false} // Prevent user from changing selection
          dragSnap={15 * 60 * 1000} // 15-minute snapping (matches bucket size)
          selected={enableEditing ? items.map((i) => i.id) : []} // All items selected = all draggable immediately
          // Event handlers
          onItemMove={enableEditing ? handleItemMove : undefined}
          onItemResize={enableEditing ? handleItemResize : undefined}
          onItemSelect={() => {}} // No-op
          moveResizeValidator={enableEditing ? moveResizeValidator : undefined}
          onTimeChange={handleTimeChange}
          // Enable mouse wheel zoom
          // @ts-expect-error - mouseZoom exists in runtime but not in type definitions
          mouseZoom={true}
          minZoom={12 * 60 * 60 * 1000} // 12 hours minimum
          maxZoom={180 * 24 * 60 * 60 * 1000} // 180 days maximum
          // Display configuration
          buffer={1}
          lineHeight={40}
          sidebarWidth={150}
          itemRenderer={itemRenderer}
        >
          <TimelineHeaders>
            <DateHeader unit="primaryHeader" />
            <DateHeader labelFormat={customLabelFormat} />
          </TimelineHeaders>
          <TimelineMarkers>
            {currentTimestamp && (
              <CustomMarker date={getTime(currentTimestamp)}>
                {({ styles }) => {
                  const customStyles = {
                    ...styles,
                    backgroundColor: theme.palette.primary.main,
                    width: "2px",
                    zIndex: 100,
                  };
                  return (
                    <div style={customStyles}>
                      <div
                        style={{
                          position: "absolute",
                          top: "-20px",
                          left: "-40px",
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {format(currentTimestamp, "MMM d, HH:mm")}
                      </div>
                    </div>
                  );
                }}
              </CustomMarker>
            )}
          </TimelineMarkers>
        </Timeline>
      </Box>

      {/* Unplaced Aircraft Section */}
      {statistics && statistics.unplacedAircraft > 0 && (
        <Box
          sx={{
            p: 1.5,
            mt: 1,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            border: 1,
            borderColor: "error.main",
          }}
          className="gantt-unplaced"
        >
          <Typography
            variant="body2"
            color="error.dark"
            fontWeight={600}
            sx={{ mb: 1 }}
          >
            ⚠ Fully Unplaced Aircraft ({statistics.unplacedAircraft}):
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {aircraft
              .filter((ac) => !placement[ac.id])
              .map((ac) => (
                <Tooltip
                  key={ac.id}
                  title={buildUnplacedAircraftTooltip(ac)}
                  arrow
                  placement="top"
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: "flip",
                          enabled: true,
                          options: {
                            fallbackPlacements: ["bottom", "right", "left"],
                            padding: 100,
                          },
                        },
                        {
                          name: "preventOverflow",
                          enabled: true,
                          options: {
                            padding: 8,
                            altAxis: true,
                          },
                        },
                      ],
                    },
                    tooltip: {
                      sx: {
                        bgcolor: "background.paper",
                        color: "text.primary",
                        boxShadow: 3,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        p: 2,
                        maxWidth: 500, // Increased from 360 to fit Decision Log content
                        "& .MuiTooltip-arrow": {
                          color: "background.paper",
                          "&::before": {
                            border: "1px solid",
                            borderColor: "divider",
                          },
                        },
                      },
                    },
                  }}
                >
                  <Chip
                    label={ac.tail_number}
                    size="small"
                    color="error"
                    variant="outlined"
                    onMouseEnter={() => handleChipMouseEnter(ac)}
                    onMouseLeave={handleChipMouseLeave}
                    onClick={() => handleChipClick(ac)}
                    sx={{
                      fontWeight: 500,
                      cursor: "pointer",
                      // Highlight if pinned
                      ...(pinnedAircraft?.id === ac.id && {
                        bgcolor: alpha(theme.palette.error.main, 0.15),
                        borderWidth: 2,
                      }),
                    }}
                  />
                </Tooltip>
              ))}
          </Stack>
        </Box>
      )}

      {/* Partially Placed Aircraft Section */}
      {(statistics?.partiallyPlacedAircraft ?? 0) > 0 && (
        <Box
          sx={{
            p: 1.5,
            mt: 1,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.warning.main, 0.1),
            border: 1,
            borderColor: "warning.main",
          }}
          className="gantt-partial"
        >
          <Typography
            variant="body2"
            color="warning.dark"
            fontWeight={600}
            sx={{ mb: 1 }}
          >
            ⚠ Partially Placed Aircraft ({statistics.partiallyPlacedAircraft}):
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1.5 }}
          >
            These aircraft are assigned to the ramp but couldn't be placed for
            their entire trip duration:
          </Typography>
          <Stack spacing={1}>
            {partialPlacements.map((partial) => {
              const gapPercent = Math.round(partial.gapPercentage);
              const placedPercent = 100 - gapPercent;

              return (
                <Box
                  key={partial.aircraftId}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    border: "1px solid rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="warning.dark"
                  >
                    {partial.tailNumber}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    Placed: {placedPercent}% (
                    {formatDuration(partial.placedDurationMinutes)}) | Gaps:{" "}
                    {gapPercent}% ({formatDuration(partial.gapDurationMinutes)})
                  </Typography>
                  {partial.gaps.map((gap, idx) => (
                    <Typography
                      key={idx}
                      variant="caption"
                      color="error.dark"
                      sx={{ display: "block", mt: 0.5, ml: 1 }}
                    >
                      • Gap {idx + 1}:{" "}
                      {formatTimeRange(gap.startTime, gap.endTime)} (
                      {formatDuration(gap.durationMinutes)})
                    </Typography>
                  ))}
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Print-Only List View (hidden on screen, visible when printing) */}
      <Box className="print-only-view">
        <SpotSchedulePrintView
          spots={spots}
          aircraft={aircraft}
          placement={placement}
          horizonHours={horizonHours}
          startTime={startTime}
          rampShapes={rampShapes}
          statistics={statistics}
          dateRange={dateRangeString}
        />
      </Box>
    </Stack>
  );
};
